type DateInput = string | Date | null | undefined;

const toValidDate = (value: DateInput): Date | null => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDate = (value: DateInput) => {
  const d = toValidDate(value);
  if (!d) return "–";

  return d.toLocaleDateString("nb-NO", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatTime = (value: DateInput) => {
  const d = toValidDate(value);
  if (!d) return "–";

  return d.toLocaleTimeString("nb-NO", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const isValidOrgnr = (value: string) => digitsOnly(value).length === 9;

export const formatOrgnr = (value: string | null) => {
  if (!value) return "–";
  return value.replace(/(.{3})/g, "$1 ");
};

export const formatOrgnrInput = (raw: string) => {
  const digits = digitsOnly(raw).slice(0, 9);
  const parts = digits.match(/.{1,3}/g) ?? [];
  return parts.join(" ");
};

export function formatFilenameTimestamp(date: Date = new Date()): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const minute = pad(date.getMinutes());
  const second = pad(date.getSeconds());
  return `${day}-${month}-${year}-${hour}-${minute}-${second}`;
}

export function formatVolumeKg(
  value: number | string | null | undefined,
  options?: {
    empty?: string;
    withUnit?: boolean;
    unit?: string;
  }
): string {
  const {empty = "", withUnit = true, unit = "kg"} = options ?? {};

  if (value === null || value === undefined || value === "") {
    return empty;
  }

  const raw = typeof value === "number" ? value.toString() : String(value).trim();
  if (!raw) return empty;

  const digitsOnly = raw.replace(/[^\d]/g, "");
  if (!digitsOnly) return empty;

  const withSpaces = digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  return withUnit ? `${withSpaces} ${unit}` : withSpaces;
}

export const parseDashboardDate = (
  input?: string | Date | null
): Date | null => {
  if (!input) return null;
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input;
  }

  const raw = input.trim();

  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    const isoTime = Date.parse(raw);
    if (!Number.isNaN(isoTime)) return new Date(isoTime);
  }

  const match =
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(
      raw
    );

  if (match) {
    const [
      ,
      dayStr,
      monthStr,
      yearStr,
      hourStr,
      minuteStr,
      secondStr,
    ] = match;

    const day = Number(dayStr);
    const month = Number(monthStr) - 1;
    const year = Number(yearStr);
    const hour = hourStr ? Number(hourStr) : 0;
    const minute = minuteStr ? Number(minuteStr) : 0;
    const second = secondStr ? Number(secondStr) : 0;

    const d = new Date(year, month, day, hour, minute, second);

    if (
      d.getFullYear() === year &&
      d.getMonth() === month &&
      d.getDate() === day
    ) {
      return d;
    }
  }
  return null;
};

export const formatNameWithUnderscore = (name: string) => {
  return name.replace(/ /g, "_");
};

export const digitsOnly = (s: string) => s.replace(/\D/g, "");

export const formatNorwegianPhone = (raw: string) => {
  const d = digitsOnly(raw).slice(0, 8);
  if (!d) return "–";
  const a = d.slice(0, 3);
  const b = d.slice(3, 5);
  const c = d.slice(5, 8);

  return [a, b, c].filter(Boolean).join(" ");
};

export const norwegianNationalPhone = (raw: string) => {
  const d = digitsOnly(raw);
  if (!d) return "–";
  if (d.length === 8) return d;
  if (d.length === 10 && d.startsWith("47")) return d.slice(2);
  if (d.length === 12 && d.startsWith("0047")) return d.slice(4);

  return d.slice(-8);
};

export const isValidNorwegianPhone = (raw: string) => digitsOnly(raw).length === 8;

export const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email.trim());

export const toNorwegianE164 = (raw: string) => {
  const d = digitsOnly(raw);
  if (!d) return "–";
  if (d.length === 8) return `47${d}`;
  if (d.length === 10 && d.startsWith("47")) return d;
  if (d.length === 12 && d.startsWith("0047")) return `47${d.slice(4)}`;
  return "";
};